import { Typography, theme } from 'antd'
import styled from 'styled-components'
import { privacyContent } from '../content/privacyContent'
import { MEDIA, TYPOGRAPHY, FONT_WEIGHT } from '../styles/theme'

const { Title, Text, Paragraph } = Typography
const { useToken } = theme

const PageWrapper = styled.div<{ $bgColor: string }>`
  min-height: 100dvh;
  background: ${(props) => props.$bgColor};
`

const Header = styled.header<{ $borderColor: string }>`
  padding: 24px 32px;
  border-bottom: 1px solid ${(props) => props.$borderColor};

  ${MEDIA.mobile} {
    padding: 16px 20px;
  }
`

const LogoLink = styled.a<{ $color: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${(props) => props.$color};
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

const ContentContainer = styled.main`
  max-width: 720px;
  margin: 0 auto;
  padding: 48px 32px 80px;

  ${MEDIA.mobile} {
    padding: 32px 20px 60px;
  }
`

const PageTitle = styled(Title)`
  && {
    margin-bottom: 8px;
  }
`

const LastUpdated = styled(Text)`
  display: block;
  margin-bottom: 48px;

  ${MEDIA.mobile} {
    margin-bottom: 32px;
  }
`

const Section = styled.section`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled(Title)`
  && {
    margin-bottom: 12px;
    font-size: ${TYPOGRAPHY.body.fontSize};
  }
`

const SubsectionTitle = styled(Title)`
  && {
    margin-top: 20px;
    margin-bottom: 8px;
    font-size: ${TYPOGRAPHY.body.fontSize};
  }
`

const SectionParagraph = styled(Paragraph)`
  && {
    margin-bottom: 12px;
    line-height: 1.7;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const Footer = styled.footer<{ $borderColor: string }>`
  padding: 24px 32px;
  border-top: 1px solid ${(props) => props.$borderColor};
  text-align: center;

  ${MEDIA.mobile} {
    padding: 20px;
  }
`

export function PrivacyPage() {
  const { token } = useToken()

  return (
    <PageWrapper $bgColor={token.colorBgContainer}>
      <Header $borderColor={token.colorBorderSecondary}>
        <LogoLink href="https://bandos.ai" $color={token.colorText}>
          Bandos
        </LogoLink>
      </Header>

      <ContentContainer>
        <PageTitle level={1}>Privacy Policy</PageTitle>
        <LastUpdated type="secondary">Last updated: {privacyContent.lastUpdated}</LastUpdated>

        {privacyContent.sections.map((section, index) => (
          <Section key={index}>
            <SectionTitle level={4}>{section.title}</SectionTitle>
            {section.content.map((paragraph, pIndex) => (
              <SectionParagraph key={pIndex} type="secondary">
                {paragraph}
              </SectionParagraph>
            ))}
            {section.subsections?.map((subsection, subIndex) => (
              <div key={subIndex}>
                <SubsectionTitle level={5}>{subsection.title}</SubsectionTitle>
                {subsection.content.map((paragraph, pIndex) => (
                  <SectionParagraph key={pIndex} type="secondary">
                    {paragraph}
                  </SectionParagraph>
                ))}
              </div>
            ))}
          </Section>
        ))}
      </ContentContainer>

      <Footer $borderColor={token.colorBorderSecondary}>
        <Text type="secondary">&copy; {new Date().getFullYear()} Bandos. All rights reserved.</Text>
      </Footer>
    </PageWrapper>
  )
}
